


import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  Autocomplete,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { API_BASE_URL } from '../../../config';
import api from '../../../api/axiosInterceptor';

const dateToDisplayString = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB').replace(/\//g, '-');
};

const convertDisplayDateToISO = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('-');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const AdminAsset52Component = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(true);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [formData, setFormData] = useState({
    asset_property_base: null,
    misc: '',
    date: '',
    court_order: '',
    result: '',
    appeals: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl. No', width: 70 },
    { id: 'name_of_fe', field: 'name_of_fe', headerName: 'Financial Establishment', width: 250 },
    { id: 'name_of_ca', field: 'name_of_ca', headerName: 'Competent Authority', width: 200 },
    { id: 'notification', field: 'notification', headerName: 'Notification', width: 150 },
    { id: 'date', field: 'date', headerName: 'Date', width: 120 },
    { id: 'notification_no', field: 'notification_no', headerName: 'Notification No', width: 150 },
    { id: 'property_detals', field: 'property_detals', headerName: 'Property Details', width: 150 },
    { id: 'survey_no', field: 'survey_no', headerName: 'Survey No', width: 120 },
    { id: 'property_type', field: 'property_type', headerName: 'Property Type', width: 150 },
    { id: 'misc', field: 'misc', headerName: 'Misc', width: 150 },
    { id: 'court_order', field: 'court_order', headerName: 'Court Order', width: 250 },
     {
      id: 'orderDate',
      field: 'orderDate',
      headerName: '5(2) Order Date',
      width: 150,
    },
    { id: 'result', field: 'result', headerName: 'Result', width: 250 },
    { id: 'appeals', field: 'appeals', headerName: 'Appeals', width: 250 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchPropertyOptions();
    fetchData();
  }, []);

  const fetchPropertyOptions = () => {
    setPropertyLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset-property/`)
      .then(res => setPropertyOptions(res.data))
      .catch(() => {
        setSnackbarMessage('Failed to load Asset Property Details options.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setPropertyLoading(false));
  };

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/asset-mgmt/asset52/`)
      .then(response => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          name_of_fe: item.asset_property_base?.asset3_2_base?.fe?.name_of_fe || '-',
          name_of_ca: item.asset_property_base?.asset3_2_base?.fe?.name_of_ca || '-',
          notification: item.asset_property_base?.asset3_2_base?.notification || '-',
          date: item.asset_property_base?.asset3_2_base?.date ? dateToDisplayString(item.asset_property_base.asset3_2_base.date) : '-',
          notification_no: item.asset_property_base?.asset3_2_base?.notification_no || '-',
          property_detals: item.asset_property_base?.property_detals || '-',
          survey_no: item.asset_property_base?.survey_no || '-',
          property_type: item.asset_property_base?.property_type || '-',
          misc: item.misc || '-',
          court_order: item.court_order || '-',
          result: item.result || '-',
          appeals: item.appeals || '-',
          asset_property_baseId: item.asset_property_base?.id || null,
          orderDate: item.date || '-',
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load Asset 5(2) data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAdd = () => {
    setIsEditMode(false);
    setSelectedRecord(null);
    setFormData({
      asset_property_base: null,
      misc: '',
      date: '',
      court_order: '',
      result: '',
      appeals: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (record) => {
    const selectedProp = propertyOptions.find(opt => opt.id === record.asset_property_baseId) || null;
    setIsEditMode(true);
    setSelectedRecord(record);
    setFormData({
      asset_property_base: selectedProp,
      misc: record.misc === '-' ? '' : record.misc,
      date: record.date && record.date !== '-' ? convertDisplayDateToISO(record.date) : '',
      court_order: record.court_order === '-' ? '' : record.court_order,
      result: record.result === '-' ? '' : record.result,
      appeals: record.appeals === '-' ? '' : record.appeals,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (record) => {
    setDeleteRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/asset-mgmt/asset52/${deleteRecord.id}/`)
      .then(() => {
        setSnackbarMessage('Record deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchData();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete record.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteRecord(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.asset_property_base) errors.asset_property_base = 'Property Details selection is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    setSubmitLoading(true);

    const payload = {
      asset_property_base: formData.asset_property_base.id,
      misc: formData.misc,
      date: formData.date || null,
      court_order: formData.court_order,
      result: formData.result,
      appeals: formData.appeals,
    };

    const request = isEditMode
      ? api.patch(`/asset-mgmt/asset52/${selectedRecord.id}/`, payload)
      : api.post(`/asset-mgmt/asset52/`, payload);

    request.then(() => {
      setSnackbarMessage(isEditMode ? 'Record updated successfully.' : 'Record added successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchData();
    }).catch(() => {
      setSnackbarMessage(isEditMode ? 'Failed to update record.' : 'Failed to add record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }).finally(() => {
      setSubmitLoading(false);
      setOpenFormDialog(false);
    });
  };

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">Admin Asset 5(2)</Typography>
        <Button variant="contained" onClick={handleAdd}>Add Record</Button>
      </Box>

      {loading ? <LoadingSpinner size={30} /> : (
        <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }, }} />
      )}

      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{isEditMode ? 'Edit Record' : 'Add Record'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Autocomplete
            options={propertyOptions}
            getOptionLabel={(option) => {
              const feName = option.asset3_2_base?.fe?.name_of_fe || '';
              const propDetals = option.property_detals || '';
              return `${feName} - ${propDetals}`;
            }}
            loading={propertyLoading}
            value={formData.asset_property_base}
            onChange={(e, newValue) => setFormData({ ...formData, asset_property_base: newValue })}
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                (option.asset3_2_base?.fe?.name_of_fe.toLowerCase().includes(inputValue.toLowerCase()) ||
                 option.property_detals.toLowerCase().includes(inputValue.toLowerCase()))
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Financial Establishment + Property Details"
                margin="dense"
                error={!!formErrors.asset_property_base}
                helperText={formErrors.asset_property_base}
                required
              />
            )}
          />
          <TextField label="Misc" value={formData.misc} onChange={e => setFormData({ ...formData, misc: e.target.value })} fullWidth margin="dense" />
          <TextField label="Date" type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
          <TextField label="Court Order" value={formData.court_order} onChange={e => setFormData({ ...formData, court_order: e.target.value })} fullWidth multiline rows={3} margin="dense" />
          <TextField label="Result" value={formData.result} onChange={e => setFormData({ ...formData, result: e.target.value })} fullWidth multiline rows={3} margin="dense" />
          <TextField label="Appeals" value={formData.appeals} onChange={e => setFormData({ ...formData, appeals: e.target.value })} fullWidth multiline rows={3} margin="dense" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit} disabled={submitLoading}>
            {submitLoading ? <LoadingSpinner size={20} /> : (isEditMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={submitLoading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
